import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { DownloadOutlined, TeamOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import moment from "moment";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];

const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-block",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
    },
    monthHeaderCell: {
        backgroundColor: "#F47E46",
        color: "#1C375D",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        border: "1px solid #fff",
        whiteSpace: "nowrap",
    },
    metricHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "12px",
        textAlign: "center",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "8px 10px",
    },
    nameHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "left",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "8px 10px",
        position: "sticky",
        left: 0,
        zIndex: 3,
    },
    nameBodyCell: {
        textAlign: "left",
        fontSize: "13px",
        fontWeight: 600,
        padding: "8px 10px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
        position: "sticky",
        left: 0,
        zIndex: 1,
        whiteSpace: "nowrap",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "8px 10px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        margin: "16px 0",
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    submitButton: {
        backgroundColor: "#F47E46 !important",
        color: "white !important",
        "&:hover": { backgroundColor: "#E0663D !important" },
    },
    clearButton: {
        backgroundColor: "#f7b7b7 !important",
        color: "#151921 !important",
    },
    clickableValue: {
        fontWeight: 700,
        cursor: "pointer",
        textDecoration: "underline",
    },
    scrollHintWrapper: {
        position: "relative",
    },
    // Anchored to the header band only (height set inline from the measured
    // thead height) so the indicator sits beside the column headers rather
    // than floating over the middle of the body rows.
    scrollHint: {
        position: "absolute",
        right: "10px",
        top: 0,
        display: "flex",
        alignItems: "center",
        zIndex: 6,
        pointerEvents: "none",
    },
    scrollHintButton: {
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        backgroundColor: "#152D4D",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
        border: "1px solid rgba(255,255,255,0.25)",
        pointerEvents: "auto",
        animation: "$pulseArrow 1.4s ease-in-out infinite",
    },
    "@keyframes pulseArrow": {
        "0%": { transform: "translateX(0)" },
        "50%": { transform: "translateX(4px)" },
        "100%": { transform: "translateX(0)" },
    },
    // Modal table styles
    modalHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.2)",
        borderBottom: "1px solid #fff",
        position: "sticky",
        top: 0,
        zIndex: 2,
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    modalBodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    modalRowEven: { backgroundColor: "#f9f9f9" },
    modalRowOdd: { backgroundColor: "#fff" },
    linkStyle: {
        textDecoration: "underline",
        color: "#1C375D",
        fontWeight: 700,
        "&:hover": { color: "#152642" },
    },
}));

// Distinct, muted "corporate" tones per metric (deep jewel tones on white,
// pale tints in the body) so a value is identifiable by column at a glance,
// even scrolled far from the header, without reading as garish.
const METRIC_COLS = [
    { key: "closure", label: "Closure", color: "#1C375D", tint: "#E9F1F9" },
    { key: "d2eTat", label: "D2E TAT", color: "#1C375D", tint: "#E9F1F9" },
    { key: "nps", label: "NPS", color: "#1C375D", tint: "#E9F1F9" },
    { key: "cancellations", label: "Cancellations", color: "#1C375D", tint: "#E9F1F9" },
];

const MODAL_COLS_BASE = [
    { label: "Sl. No.", key: "slno" },
    { label: "Customer Name", key: "customerName" },
    { label: "Project No.", key: "erpProjectNo" },
    { label: "Lead No.", key: "lead_no" },
    { label: "Value (INR L)", key: "value" },
    { label: "City", key: "cityName" },
    { label: "Exp. Ctr. Name", key: "experienceCenterName" },
    { label: "Current Stage", key: "currentStage" },
];

const MODAL_COLS_CANCELLATION_EXTRA = [
    { label: "Remarks", key: "remarks" },
];

const MODAL_COLS_NPS_EXTRA = [
    { label: "NPS Design Score", key: "npsDesignScore" },
    { label: "Survey Date", key: "surveyDate", isDate: true },
    { label: "NPS Points", key: "npsPoints" },
    { label: "Remarks", key: "remarks" },
];

function getModalCols(type) {
    if (type === "cancellations") return [...MODAL_COLS_BASE, ...MODAL_COLS_CANCELLATION_EXTRA];
    if (type === "nps") return [...MODAL_COLS_BASE, ...MODAL_COLS_NPS_EXTRA];
    return MODAL_COLS_BASE; // closure
}

function normalizeRows(leads = [], cols = MODAL_COLS_BASE) {
    return leads.map((lead, idx) => {
        const row = { ...lead, slno: idx + 1 };
        cols.forEach((col) => {
            if (col.isDate) row[col.key] = row[col.key] ? moment(row[col.key]).format("DD-MM-YYYY") : "-";
        });
        return row;
    });
}

export default function MyTeamPerformance() {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isAllowed = ALLOWED_ROLES.includes(userRoleName);
    const isAGM = userRoleName === ROLENAME.AGM_Design;

    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState([]);
    const [rows, setRows] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ cities: [], experienceCenters: [] });

    const [filters, setFilters] = useState({
        city: "",
        experienceCenter: "",
        designation: isAGM ? "ODM" : "Designer",
        activeStatus: "active",
    });

    const [modal, setModal] = useState({ open: false, title: "", type: "", leads: [] });

    const tableContainerRef = useRef(null);
    const theadRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);

    const checkScroll = () => {
        const el = tableContainerRef.current;
        if (!el) return;
        setShowScrollHint(el.scrollWidth - el.clientWidth - el.scrollLeft > 10);
    };

    const measureHeader = () => {
        if (theadRef.current) setHeaderHeight(theadRef.current.offsetHeight);
    };

    const scrollRight = () => {
        tableContainerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    };

    useEffect(() => {
        fetchTeamPerformance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        checkScroll();
        measureHeader();
        window.addEventListener("resize", measureHeader);
        return () => window.removeEventListener("resize", measureHeader);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, months, loading]);

    const fetchTeamPerformance = async (appliedFilters = filters) => {
        setLoading(true);
        try {
            const res = await HttpService.getMyTeamPerformance(appliedFilters);
            if (res.data?.success) {
                setMonths(res.data.months || []);
                setRows(res.data.rows || []);
                setFilterOptions(res.data.filters || { cities: [], experienceCenters: [] });
            }
        } catch (err) {
            console.error("Error fetching my team performance:", err);
        }
        setLoading(false);
    };

    const handleFilterChange = (name, value) => {
        setFilters((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "city") next.experienceCenter = ""; // EC depends on City
            return next;
        });
    };

    const applyFilters = () => fetchTeamPerformance(filters);

    const clearFilters = () => {
        const def = { city: "", experienceCenter: "", designation: isAGM ? "ODM" : "Designer", activeStatus: "active" };
        setFilters(def);
        fetchTeamPerformance(def);
    };

    const availableExperienceCenters = filters.city
        ? filterOptions.experienceCenters.filter((ec) => ec.city === filters.city)
        : filterOptions.experienceCenters;

    const openModal = (title, leads, type) => setModal({ open: true, title, type, leads: leads || [] });
    const closeModal = () => setModal({ open: false, title: "", type: "", leads: [] });

    const modalCols = getModalCols(modal.type);
    const modalRows = normalizeRows(modal.leads, modalCols);
    const modalCsvHeaders = modalCols.map((c) => ({ label: c.label, key: c.key }));

    const getCellValue = (row, m, c) => {
        const cell = row.months?.[m.key]?.[c.key];
        if (c.key === "d2eTat") return cell === null || cell === undefined ? null : cell;
        if (c.key === "nps") return cell?.value ?? null;
        return cell?.count ?? 0; // closure / cancellations
    };

    const csvHeaders = [
        { label: "Name", key: "name" },
        { label: "Designation", key: "designation" },
        ...months.flatMap((m) => METRIC_COLS.map((c) => ({ label: `${m.label} ${c.label}`, key: `${m.key}_${c.key}` }))),
    ];

    const csvData = rows.map((row) => {
        const flat = { name: row.name, designation: row.designation };
        months.forEach((m) => {
            METRIC_COLS.forEach((c) => {
                const val = getCellValue(row, m, c);
                flat[`${m.key}_${c.key}`] = val === null || val === undefined ? "-" : val;
            });
        });
        return flat;
    });

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <TeamOutlined className={classes.titleIcon} />
                    My Team Performance
                </Typography>
                <Typography className={classes.subtitle}>
                    Month-wise team performance for the current year — click Submit after changing filters
                </Typography>
            </div>

            <Paper elevation={2} style={{ padding: "16px", marginBottom: "24px" }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="city-label">City</InputLabel>
                            <Select
                                labelId="city-label"
                                label="City"
                                value={filters.city}
                                onChange={(e) => handleFilterChange("city", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {filterOptions.cities.map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="ec-label">Experience Centre</InputLabel>
                            <Select
                                labelId="ec-label"
                                label="Experience Centre"
                                value={filters.experienceCenter}
                                onChange={(e) => handleFilterChange("experienceCenter", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {availableExperienceCenters.map((ec) => (
                                    <MenuItem key={ec._id} value={ec._id}>{ec.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {isAGM && (
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel id="designation-label">Designation</InputLabel>
                                <Select
                                    labelId="designation-label"
                                    label="Designation"
                                    value={filters.designation}
                                    onChange={(e) => handleFilterChange("designation", e.target.value)}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    <MenuItem value="Designer">Designer</MenuItem>
                                    <MenuItem value="ODM">ODM</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={3}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="active-status-label">Status</InputLabel>
                            <Select
                                labelId="active-status-label"
                                label="Status"
                                value={filters.activeStatus}
                                onChange={(e) => handleFilterChange("activeStatus", e.target.value)}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                            <Button variant="contained" className={classes.clearButton} onClick={clearFilters}>Clear</Button>
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {rows.length > 0 && (
                <div className={classes.csv}>
                    <CSVLink data={csvData} headers={csvHeaders} filename={`My_Team_Performance_${new Date().toISOString().split("T")[0]}.csv`} style={{ textDecoration: "none" }}>
                        <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                            <DownloadOutlined /> Download CSV
                        </button>
                    </CSVLink>
                </div>
            )}

            {loading ? (
                <Typography className={classes.noData}>Loading...</Typography>
            ) : rows.length === 0 ? (
                <Typography className={classes.noData}>No team members found for the selected filters</Typography>
            ) : (
                <div className={classes.scrollHintWrapper}>
                    <TableContainer
                        ref={tableContainerRef}
                        onScroll={checkScroll}
                        style={{ maxWidth: "100%", overflowX: "auto" }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead ref={theadRef}>
                                <TableRow>
                                    <TableCell className={classes.nameHeaderCell} rowSpan={2} style={{ zIndex: 4 }}>Name</TableCell>
                                    {months.map((m) => (
                                        <TableCell key={m.key} className={classes.monthHeaderCell} colSpan={METRIC_COLS.length}>
                                            {m.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    {months.map((m) =>
                                        METRIC_COLS.map((c) => (
                                            <TableCell
                                                key={`${m.key}-${c.key}`}
                                                className={classes.metricHeaderCell}
                                                style={{ backgroundColor: c.color }}
                                            >
                                                {c.label}
                                            </TableCell>
                                        ))
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.userId}>
                                        <TableCell className={classes.nameBodyCell}>{row.name}</TableCell>
                                        {months.map((m) =>
                                            METRIC_COLS.map((c) => {
                                                const value = getCellValue(row, m, c);
                                                const cellData = row.months?.[m.key]?.[c.key];
                                                const clickable = c.key !== "d2eTat" && cellData?.data?.length > 0;
                                                return (
                                                    <TableCell
                                                        key={`${row.userId}-${m.key}-${c.key}`}
                                                        className={classes.bodyCell}
                                                        style={{ backgroundColor: c.tint }}
                                                    >
                                                        {value === null || value === undefined ? (
                                                            "-"
                                                        ) : clickable ? (
                                                            <span
                                                                className={classes.clickableValue}
                                                                style={{ color: c.color }}
                                                                onClick={() => openModal(`${row.name} — ${m.label} ${c.label}`, cellData.data, c.key)}
                                                            >
                                                                {value}
                                                            </span>
                                                        ) : (
                                                            value
                                                        )}
                                                    </TableCell>
                                                );
                                            })
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {showScrollHint && (
                        <div className={classes.scrollHint} style={{ height: headerHeight || undefined }}>
                            <div className={classes.scrollHintButton} onClick={scrollRight}>
                                <ChevronRightIcon fontSize="small" />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Detail Modal ───────────────────────────────────────────────── */}
            <Dialog open={modal.open} onClose={closeModal} maxWidth="xl" fullWidth
                PaperProps={{ style: { borderRadius: "10px", maxHeight: "90vh" } }}>
                <DialogTitle disableTypography style={{ backgroundColor: "#1C375D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
                    <Typography style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>
                        {modal.title}
                        <span style={{ marginLeft: 12, fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>
                            ({modalRows.length} records)
                        </span>
                    </Typography>
                    <IconButton size="small" onClick={closeModal} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: "16px 20px" }}>
                    {modalRows.length > 0 && (
                        <div className={classes.csv}>
                            <CSVLink data={modalRows} headers={modalCsvHeaders}
                                filename={`${modal.title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`}
                                style={{ textDecoration: "none" }}>
                                <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                    <DownloadOutlined /> Download CSV
                                </button>
                            </CSVLink>
                        </div>
                    )}
                    {modalRows.length === 0 ? (
                        <Typography className={classes.noData}>No data found</Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {modalCols.map((col) => (
                                            <TableCell key={col.key} className={classes.modalHeaderCell}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modalRows.map((row, idx) => (
                                        <TableRow key={row.leadId || idx} className={idx % 2 === 0 ? classes.modalRowEven : classes.modalRowOdd}>
                                            {modalCols.map((col) => (
                                                <TableCell key={col.key} className={classes.modalBodyCell}>
                                                    {(col.key === "lead_no" || col.key === "erpProjectNo") && row.leadId ? (
                                                        <Link
                                                            to={`/project-logs/${row.leadId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classes.linkStyle}
                                                        >
                                                            {row[col.key]}
                                                        </Link>
                                                    ) : row[col.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
